import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/blocks/evo-sidebar";
import { Settings, ChevronRight, Shield, FileText } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { useFavorites } from "./hooks/useFavorites";
import { DatabaseSwitcher } from "./components/DatabaseSwitcher";
import { FavoritesSection } from "./components/FavoritesSection";
import { MenuItemWithContext } from "./components/MenuItemWithContext";
import type { LeftMenuProps, MenuItem } from "./types";
import { menuItems, databases, menuConfig } from "./config/menuItems";

/**
 * Left Menu Component (Modular Version)
 * 
 * Features:
 * - Collapsible sidebar (icon-only mode)
 * - Favorites system (disabled - only 9 pages, threshold is 10)
 * - Database/Environment switcher (disabled - company selector in header)
 * - Context menu for favoriting items
 * - Mobile responsive
 * - Keyboard shortcuts (Cmd/Ctrl + B)
 */
export function LeftMenuModular({ activePage, onPageChange }: LeftMenuProps) {
  const [selectedDatabase, setSelectedDatabase] = useState("production");
  const [activeItemId, setActiveItemId] = useState<string>("");
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    "resources-menu": false, // Resources menu collapsed by default
    "reports-menu": false, // Reports menu collapsed by default
    "settings": false, // Settings menu collapsed by default
  });

  const favoritesEnabled = menuConfig.totalPages >= menuConfig.favoritesThreshold;
  const { favorites, toggleFavorite, isFavorite } = useFavorites(favoritesEnabled);

  const favoriteItems = menuItems.filter((item) => isFavorite(item.id));

  // Sync activeItemId with activePage prop
  useEffect(() => {
    // Map page names to their menu item IDs
    const pageToItemId: Record<string, string> = {
      'dashboard': 'main-dashboard',
      'projects': 'main-projects',
      'project-templates': 'main-project-templates',
      'portfolio': 'main-portfolio',
      'directory': 'sub-directory',
      'roles-rates': 'sub-roles-rates',
      'resources': 'sub-resources',
      'utilisation': 'sub-utilisation',
      'project-transactions': 'sub-project-transactions',
      'wip-reporting': 'sub-wip-reporting',
      'profitability': 'main-profitability',
      'budgets': 'main-budgets',
      'gantt': 'main-gantt',
      'settings-general': 'sub-settings-general',
      'settings-project': 'sub-settings-project',
      'settings-user-preferences': 'sub-settings-user-preferences',
      'settings-integrations': 'sub-settings-integrations',
      'settings-feature-labs': 'sub-settings-feature-labs',
      'settings-user-permissions': 'sub-settings-user-permissions',
      'settings-templates': 'sub-settings-templates',
    };
    
    const itemId = pageToItemId[activePage];
    if (itemId) {
      setActiveItemId(itemId);
      
      // Auto-open parent menus for sub-items
      if (itemId.startsWith('sub-settings-')) {
        setOpenMenus(prev => ({ ...prev, 'settings': true }));
      } else if (itemId.startsWith('sub-') && activePage.includes('directory')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-') && activePage.includes('roles-rates')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-resources')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-utilisation')) {
        setOpenMenus(prev => ({ ...prev, 'resources-menu': true }));
      } else if (itemId.startsWith('sub-project-transactions') || itemId.startsWith('sub-wip-reporting')) {
        setOpenMenus(prev => ({ ...prev, 'reports-menu': true }));
      }
    }
  }, [activePage]);

  const handleItemClick = (page: string, itemId: string) => {
    setActiveItemId(itemId);
    onPageChange(page);
    
    // Close Resources, Reports, and Settings menus when clicking on any main menu item (not sub-items)
    if (!itemId.startsWith('sub-')) {
      setOpenMenus(prev => ({
        ...prev,
        "resources-menu": false,
        "reports-menu": false,
        "settings": false
      }));
    }
  };

  const toggleMenu = (menuId: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const renderMenuItem = (item: MenuItem, uniqueId: string, isFavoriteItem: boolean = false) => {
    const isActive = activeItemId === uniqueId;
    return (
      <MenuItemWithContext
        item={item}
        uniqueId={uniqueId}
        isActive={isActive}
        isFavoriteItem={isFavoriteItem}
        isFavorite={isFavorite(item.id)}
        onItemClick={handleItemClick}
        onToggleFavorite={toggleFavorite}
        favoritesEnabled={favoritesEnabled}
      />
    );
  };

  return (
    <Sidebar variant="floating" collapsible="icon" className="relative h-full">
      {/* Header */}
      <SidebarHeader>
        <div className="flex items-center gap-2">
          <SidebarTrigger />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[195px] group-data-[state=collapsed]:hidden text-neutral-700">
            Projects & Billing
          </h3>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show"></div>

      {/* Content */}
      <SidebarContent className="group-data-[state=collapsed]:items-center">
        {/* Favorites Section - Only shows if enabled (currently disabled: 9 pages < 10 threshold) */}
        {favoritesEnabled && (
          <FavoritesSection favoriteItems={favoriteItems} renderMenuItem={renderMenuItem} />
        )}

        {/* Main Navigation Section */}
        <SidebarGroup className="group-data-[state=collapsed]:items-center">
          <SidebarGroupContent className="group-data-[state=collapsed]:w-8">
            <SidebarMenu className="gap-0.5 group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">
              
              {/* Dashboard */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "dashboard")!, "main-dashboard")}
              </SidebarMenuItem>

              {/* Projects */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "projects")!, "main-projects")}
              </SidebarMenuItem>

              {/* Project Templates */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "project-templates")!, "main-project-templates")}
              </SidebarMenuItem>

              {/* Portfolio */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "portfolio")!, "main-portfolio")}
              </SidebarMenuItem>

              {/* Resources Menu (Collapsible with sub-items) */}
              {(() => {
                const resourcesMenu = menuItems.find((item) => item.id === "resources-menu")!;
                const ResourcesIcon = resourcesMenu.icon;
                const DirectoryIcon = resourcesMenu.children![0].icon;
                const RolesRatesIcon = resourcesMenu.children![1].icon;
                const ResourceAllocationIcon = resourcesMenu.children![2].icon;
                const UtilisationIcon = resourcesMenu.children![3].icon;
                
                return (
                  <SidebarMenuItem>
                    <Collapsible
                      open={openMenus["resources-menu"]}
                      onOpenChange={() => toggleMenu("resources-menu")}
                      className="group/collapsible"
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Resources">
                          <ResourcesIcon />
                          <span className="group-data-[collapsible=icon]:hidden">Resources</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Directory */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("directory", "sub-directory")}
                              data-active={activeItemId === "sub-directory"}
                              className={activeItemId === "sub-directory" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <DirectoryIcon />
                              <span>Directory</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Roles & Rates */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("roles-rates", "sub-roles-rates")}
                              data-active={activeItemId === "sub-roles-rates"}
                              className={activeItemId === "sub-roles-rates" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <RolesRatesIcon />
                              <span>Roles & Rates</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Resource Allocation */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("resources", "sub-resources")}
                              data-active={activeItemId === "sub-resources"}
                              className={activeItemId === "sub-resources" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <ResourceAllocationIcon />
                              <span>Resource Allocation</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* Utilisation */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("utilisation", "sub-utilisation")}
                              data-active={activeItemId === "sub-utilisation"}
                              className={activeItemId === "sub-utilisation" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <UtilisationIcon />
                              <span>Utilisation</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarMenuItem>
                );
              })()}

              {/* Reports Menu (Collapsible with sub-items) */}
              {(() => {
                const reportsMenu = menuItems.find((item) => item.id === "reports-menu")!;
                const ReportsIcon = reportsMenu.icon;
                const ProjectTransactionsIcon = reportsMenu.children![0].icon;
                const WIPIcon = reportsMenu.children![1].icon;
                
                return (
                  <SidebarMenuItem>
                    <Collapsible
                      open={openMenus["reports-menu"]}
                      onOpenChange={() => toggleMenu("reports-menu")}
                      className="group/collapsible"
                    >
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip="Reports">
                          <ReportsIcon />
                          <span className="group-data-[collapsible=icon]:hidden">Reports</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {/* Project Transactions */}
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick("project-transactions", "sub-project-transactions")}
                              data-active={activeItemId === "sub-project-transactions"}
                              className={activeItemId === "sub-project-transactions" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                            >
                              <ProjectTransactionsIcon />
                              <span>Project Transactions</span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          {/* WIP (Work In Progress) */}
                          <SidebarMenuSubItem>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    onClick={() => handleItemClick("wip-reporting", "sub-wip-reporting")}
                                    data-active={activeItemId === "sub-wip-reporting"}
                                    className={activeItemId === "sub-wip-reporting" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                                  >
                                    <WIPIcon />
                                    <span>WIP</span>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Work In Progress</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  </SidebarMenuItem>
                );
              })()}

              {/* Profitability */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "profitability")!, "main-profitability")}
              </SidebarMenuItem>

              {/* Budgets & Variance */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "budgets")!, "main-budgets")}
              </SidebarMenuItem>

              {/* Gantt Charts */}
              <SidebarMenuItem>
                {renderMenuItem(menuItems.find((item) => item.id === "gantt")!, "main-gantt")}
              </SidebarMenuItem>

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter>
        {/* Settings Menu (Collapsible with sub-items) */}
        {menuConfig.showSettings && (
          <SidebarMenu>
            <SidebarMenuItem>
              <Collapsible
                open={openMenus["settings"]}
                onOpenChange={() => toggleMenu("settings")}
                className="group/collapsible"
              >
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip="Settings">
                    <Settings />
                    <span>Settings</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {/* General Settings */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        onClick={() => handleItemClick("settings-general", "sub-settings-general")}
                        data-active={activeItemId === "sub-settings-general"}
                        className={activeItemId === "sub-settings-general" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                      >
                        <Settings />
                        <span>General Settings</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    {/* Project Settings */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        onClick={() => handleItemClick("settings-project", "sub-settings-project")}
                        data-active={activeItemId === "sub-settings-project"}
                        className={activeItemId === "sub-settings-project" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                      >
                        <Settings />
                        <span>Project Settings</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    {/* User Preferences */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        onClick={() => handleItemClick("settings-user-preferences", "sub-settings-user-preferences")}
                        data-active={activeItemId === "sub-settings-user-preferences"}
                        className={activeItemId === "sub-settings-user-preferences" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                      >
                        <Settings />
                        <span>User Preferences</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    {/* Integrations */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        onClick={() => handleItemClick("settings-integrations", "sub-settings-integrations")}
                        data-active={activeItemId === "sub-settings-integrations"}
                        className={activeItemId === "sub-settings-integrations" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                      >
                        <Settings />
                        <span>Integrations</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>

                    {/* Feature Labs */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        onClick={() => handleItemClick("settings-feature-labs", "sub-settings-feature-labs")}
                        data-active={activeItemId === "sub-settings-feature-labs"}
                        className={activeItemId === "sub-settings-feature-labs" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                      >
                        <Settings />
                        <span>Feature Labs</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>

                    {/* User Permissions */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        onClick={() => handleItemClick("settings-user-permissions", "sub-settings-user-permissions")}
                        data-active={activeItemId === "sub-settings-user-permissions"}
                        className={activeItemId === "sub-settings-user-permissions" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                      >
                        <Shield />
                        <span>User Permissions</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>

                    {/* Templates */}
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        onClick={() => handleItemClick("settings-templates", "sub-settings-templates")}
                        data-active={activeItemId === "sub-settings-templates"}
                        className={activeItemId === "sub-settings-templates" ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground" : ""}
                      >
                        <FileText />
                        <span>Templates</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </Collapsible>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {/* Database Switcher - Not used (company selector in header) */}
        {menuConfig.showDatabaseSwitcher && (
          <>
            <div className="border-t border-neutral-200 mb-2 mt-2 group-data-[state=collapsed]:hidden"></div>
            <DatabaseSwitcher
              databases={databases}
              selectedDatabase={selectedDatabase}
              onDatabaseChange={setSelectedDatabase}
              label="Database"
            />
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
